import { useState } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConnection, useWriteContract } from "wagmi";
import { useQueryClient } from "@tanstack/react-query";
import { simulateContract, waitForTransactionReceipt } from "@wagmi/core";
import { wagmiConfig } from "@/config";
import { FORTTRADE_CONTRACT_ADDRESS } from "@/config/env";
import { FortTradeAbi } from "@/abi/FortTrade.abi";
import {
  FortAgentActivateResultCode,
  FORT_AGENT_STATUS,
  MyFortAgentItem,
} from "@/api/types";
import { showError, showSuccess } from "@/lib/toast";
import { parseChainError } from "@/lib/chainError";
import { useFortAgentActivateSign } from "@/hooks/api/useFortAgentActivateSign";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import { AGENT_IMAGE_URL } from "./constants";
import { MyAgentCardActivatedPanel } from "./MyAgentCardActivatedPanel";
import { MyAgentCardInactivePanel } from "./MyAgentCardInactivePanel";

export function MyAgentCard({
  agent,
  onAddress,
  onTransfer,
  hasActivatedAgent,
}: {
  agent: MyFortAgentItem;
  onAddress: () => void;
  onTransfer: () => void;
  /** 账户内是否已有 status=已激活 的智能体（仅一台可激活时，待激活卡需隐藏激活类操作） */
  hasActivatedAgent: boolean;
}) {
  const { t } = useTranslation();
  const { address } = useConnection();
  const writeContract = useWriteContract();
  const { mutateAsync: activateSignAsync } = useFortAgentActivateSign();
  const [activating, setActivating] = useState(false);
  const { openPayFusd } = useMyAgentsUiStore();
  const queryClient = useQueryClient();

  const mustPayFusdBeforeActivate =
    agent.paymentMode === "mixed" &&
    !agent.fusdPurchaseDeducted &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  const handleActivate = async () => {
    if (!address) {
      showError(t("toast.common.connectWalletFirst"));
      return;
    }
    setActivating(true);
    try {
      const res = await activateSignAsync({ agentId: agent.agentId });
      if (res.resultCode !== FortAgentActivateResultCode.Success) return;

      const { request } = await simulateContract(wagmiConfig, {
        address: FORTTRADE_CONTRACT_ADDRESS as `0x${string}`,
        abi: FortTradeAbi,
        functionName: "activate",
        args: [
          BigInt(res.agentId),
          BigInt(res.deadline),
          BigInt(res.nonce),
          res.signature as `0x${string}`,
        ],
      });
      const hash = await writeContract.mutateAsync(request);
      await waitForTransactionReceipt(wagmiConfig, { hash });
      showSuccess(t("toast.fortAgent.activate.result.success"));
      queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
    } catch (err) {
      console.error("activateErr", err);
      const errorKey = parseChainError(err);
      showError(t(errorKey));
    } finally {
      setActivating(false);
    }
  };

  const onActivateClick = () => {
    if (!address) {
      showError(t("toast.common.connectWalletFirst"));
      return;
    }
    return showError(t("homePage.myAgentCard.viewDetailSoon"));
    if (mustPayFusdBeforeActivate) {
      openPayFusd(agent.agentId);
      return;
    }
    void handleActivate();
  };

  const isActivated = agent.status === FORT_AGENT_STATUS.ACTIVATED;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden relative"
      style={{
        background: isActivated
          ? "linear-gradient(135deg, rgba(34,197,94,0.015) 0%, #0c1020 40%, #080c18 100%)"
          : "linear-gradient(135deg, rgba(212,165,116,0.015) 0%, #0c1020 40%, #080c18 100%)",
        border: isActivated
          ? "1px solid rgba(34,197,94,0.07)"
          : "1px solid rgba(212,165,116,0.07)",
        boxShadow: isActivated
          ? "0 2px 20px rgba(34,197,94,0.02)"
          : "0 2px 20px rgba(212,165,116,0.02)",
      }}
    >
      <div
        className="h-px"
        style={{
          background: isActivated
            ? "linear-gradient(90deg, transparent 10%, rgba(34,197,94,0.08) 50%, transparent 90%)"
            : "linear-gradient(90deg, transparent 10%, rgba(212,165,116,0.08) 50%, transparent 90%)",
        }}
      />
      <div className="flex" style={{ minHeight: 0 }}>
        {isActivated ? (
          <div
            className="relative flex flex-col items-center justify-center overflow-hidden"
            style={{
              width: "42%",
              minHeight: 0,
              backgroundColor: "#0a0e1a",
              backgroundImage:
                "radial-gradient(ellipse at center 60%, rgba(147,51,234,0.12) 0%, rgba(34,197,94,0.08) 35%, #0a0e1a 70%)",
            }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full blur-[50px] glow-breathe"
                style={{ background: "rgba(147,51,234,0.15)" }}
              />
              <div
                className="absolute top-1/3 right-1/4 w-12 h-12 rounded-full blur-[25px] glow-breathe"
                style={{
                  background: "rgba(168,85,247,0.10)",
                  animationDelay: "1.5s",
                }}
              />
              <div
                className="absolute bottom-1/4 left-1/4 w-10 h-10 rounded-full blur-[20px] glow-breathe"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  animationDelay: "2.5s",
                }}
              />
            </div>
            <div
              className="relative z-10 flex items-center justify-center agent-float"
              style={{ width: "90%", height: "90%" }}
            >
              <img
                src={AGENT_IMAGE_URL}
                alt="AI Agent"
                className="w-full h-full object-contain"
                style={{
                  filter:
                    "drop-shadow(0 0 14px rgba(147,51,234,0.3)) drop-shadow(0 0 24px rgba(34,197,94,0.14))",
                }}
              />
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1">
              <div
                className="w-full h-full rounded-full energy-pulse"
                style={{
                  background:
                    "radial-gradient(ellipse, rgba(147,51,234,0.4) 0%, rgba(168,85,247,0.18) 40%, transparent 70%)",
                }}
              />
            </div>
          </div>
        ) : (
          <div
            className="relative flex flex-col items-center justify-center overflow-hidden"
            style={{
              width: "42%",
              minHeight: "200px",
              background: "rgba(212,165,116,0.01)",
            }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div
                className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full blur-[40px]"
                style={{ background: "rgba(100,116,139,0.04)" }}
              />
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] w-28 h-28">
              <div
                className="w-full h-full rounded-full"
                style={{ border: "1px solid rgba(100,116,139,0.05)" }}
              />
            </div>
            <div
              className="relative z-10 flex items-center justify-center"
              style={{ width: "90%", height: "90%" }}
            >
              <img
                src={AGENT_IMAGE_URL}
                alt="AI Agent"
                className="w-full h-full object-contain"
                style={{
                  filter: "grayscale(100%) brightness(0.7)",
                  opacity: 0.4,
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(0,0,0,0.45)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid rgba(212,165,116,0.20)",
                  }}
                >
                  <Lock size={14} style={{ color: "#d4a574" }} />
                </div>
              </div>
            </div>
            <div className="relative w-14 h-1 -mt-0.5">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse, rgba(100,116,139,0.10) 0%, transparent 70%)",
                }}
              />
            </div>
          </div>
        )}

        <div
          className="flex-1 py-3.5 pr-3.5 pl-1 space-y-2"
          style={{ minWidth: 0 }}
        >
          {isActivated ? (
            <MyAgentCardActivatedPanel agent={agent} onAddress={onAddress} />
          ) : (
            <MyAgentCardInactivePanel
              agent={agent}
              onAddress={onAddress}
              onTransfer={onTransfer}
              onActivateClick={onActivateClick}
              mustPayFusdBeforeActivate={mustPayFusdBeforeActivate}
              activating={activating}
              hasActivatedAgent={hasActivatedAgent}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}
